/**
 * 传输抽象。
 *
 * 只做一件事：**把字节搬进搬出**，并把"通道现在是什么状态、为什么"如实报告上来。
 * 它不理解 MAVLink，不解析帧，不做重连策略以外的任何判断 ——
 * 这条界线让上层（会话、车辆运行时、界面）对底层是串口还是 WebSocket 完全无感。
 *
 * 四种实现共享同一份契约：
 *   - Web Serial：USB 直连飞控
 *   - WebSocket：网络链路（伴飞计算机、4G 数传、mavlink2rest 风格的网关）
 *   - Web Bluetooth：BLE 透传模块
 *   - 内置模拟机：浏览器内产生**真实 MAVLink 帧**，用于开发、联调、演示与自动化测试
 *
 * 模拟机走的是同一条路径、同一套编解码，因此"没有真机也能把整条链路和全部界面验收完"
 * 不是一句口号：真机接入时改的只是这个接口的实现。
 */

export type TransportKind = 'serial' | 'websocket' | 'bluetooth' | 'simulated' | 'replay';

export type TransportState =
  /** 未打开，或已主动关闭 */
  | 'closed'
  /** 正在打开（用户授权、DNS、握手……） */
  | 'opening'
  | 'open'
  /** 正在关闭 */
  | 'closing'
  /** 打开失败或中途断开，`detail` 里有可读原因 */
  | 'error';

export interface TransportStateChange {
  readonly state: TransportState;
  /**
   * 给人看的中文短句。**必须**在失败时填 ——
   * "连不上但没有原因"会让使用者只能靠猜，而这正是排障成本最高的地方。
   */
  readonly detail?: string;
}

export type TransportEvent =
  | { readonly type: 'data'; readonly bytes: Uint8Array }
  | ({ readonly type: 'state' } & TransportStateChange);

export type TransportListener = (event: TransportEvent) => void;

/**
 * 一条链路。
 *
 * `open` / `close` / `write` 都可能因为底层拒绝而抛错，调用方需要处理；
 * 而**连接后的意外断开通过状态事件报告**，不通过 Promise —— 断开可能发生在任何时候，
 * 而 `open()` 早就返回了。
 */
export interface Transport {
  readonly kind: TransportKind;
  /** 人类可读的标识，例如 `ttyUSB0 @ 115200` 或 `wss://drone.local:8080/mavlink` */
  readonly label: string;
  readonly state: TransportState;

  open(): Promise<void>;
  close(): Promise<void>;
  /** 写入一段字节。实现负责保证顺序，不保证原子性。 */
  write(bytes: Uint8Array): Promise<void>;

  /** 订阅数据与状态事件，返回取消订阅函数 */
  subscribe(listener: TransportListener): () => void;

  /**
   * 链路质量的"原始输入" —— 由实现自报，而不是由上层猜。
   * 串口没有这个概念就返回 `null`；WebSocket 可以用 ping 往返；BLE 用 RSSI。
   */
  linkMetrics?(): TransportMetrics | null;
}

export interface TransportMetrics {
  /** 往返时延（毫秒），没有测法时为 null */
  readonly latencyMs: number | null;
  /** 信号强度（dBm），不适用时为 null */
  readonly rssi: number | null;
}

/** 所有实现共用的最小底座：监听器管理与状态迁移。 */
export abstract class BaseTransport implements Transport {
  abstract readonly kind: TransportKind;
  abstract readonly label: string;

  protected currentState: TransportState = 'closed';
  private readonly listeners = new Set<TransportListener>();

  get state(): TransportState {
    return this.currentState;
  }

  subscribe(listener: TransportListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  protected emit(event: TransportEvent): void {
    for (const listener of this.listeners) listener(event);
  }

  protected emitData(bytes: Uint8Array): void {
    if (bytes.length === 0) return;
    this.emit({ type: 'data', bytes });
  }

  protected setState(state: TransportState, detail?: string): void {
    if (this.currentState === state) return;
    this.currentState = state;
    this.emit(detail === undefined ? { type: 'state', state } : { type: 'state', state, detail });
  }

  abstract open(): Promise<void>;
  abstract close(): Promise<void>;
  abstract write(bytes: Uint8Array): Promise<void>;
}

/**
 * 指数退避重连的计算。
 *
 * 抽成纯函数是为了能直接单测：重连策略写错的表现是"隔很久才恢复"或"疯狂重试把设备打挂"，
 * 两种都不容易在联调时被注意到。
 */
export interface BackoffOptions {
  readonly initialMs: number;
  readonly maxMs: number;
  /** 倍数，默认 2 */
  readonly factor?: number;
}

export function backoffDelay(attempt: number, options: BackoffOptions): number {
  const factor = options.factor ?? 2;
  const raw = options.initialMs * factor ** Math.max(0, attempt);
  return Math.min(raw, options.maxMs);
}
